// =============================================================================
// src/utils/appointmentMessages.js
// WhatsApp messages for appointments.
//   Client: confirmation, reminder, reschedule, cancellation, thank-you
//   Staff:  new booking for you, today's / day schedule
// Opens WhatsApp with the message ready (wa.me), no API cost and no template approval needed.
// =============================================================================

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-10-04" -> "Sat, 04 Oct 2026" */
export const prettyDate = (ymd) => {
    const [y, m, d] = String(ymd || '').split('-').map(Number);
    if (!y || !m || !d) return String(ymd || '');
    const dt = new Date(y, m - 1, d);
    return `${DAY_NAMES[dt.getDay()]}, ${String(d).padStart(2, '0')} ${MONTHS[m - 1]} ${y}`;
};

/** "14:30" -> "02:30 PM" */
export const prettyTime = (hhmm) => {
    const [h, mi] = String(hhmm || '').split(':').map(Number);
    if (!Number.isFinite(h)) return String(hhmm || '');
    const ampm = h < 12 ? 'AM' : 'PM';
    return `${String(h % 12 || 12).padStart(2, '0')}:${String(mi || 0).padStart(2, '0')} ${ampm}`;
};

/** Indian 10-digit numbers get the 91 country code. Returns '' if not a usable number. */
export const toWhatsAppNumber = (phone) => {
    const digits = String(phone || '').replace(/\D/g, '');
    if (digits.length === 10) return `91${digits}`;
    if (digits.length === 12 && digits.startsWith('91')) return digits;
    if (digits.length === 11 && digits.startsWith('0')) return `91${digits.slice(1)}`;
    return digits.length >= 11 ? digits : '';
};

export const whatsappUrl = (phone, text) => {
    const number = toWhatsAppNumber(phone);
    const q = `text=${encodeURIComponent(text)}`;
    return number ? `https://wa.me/${number}?${q}` : `https://wa.me/?${q}`;
};

/** Opens WhatsApp in a new tab (or the app on mobile) */
export const openWhatsApp = (phone, text) => {
    window.open(whatsappUrl(phone, text), '_blank', 'noopener,noreferrer');
};

const firstName = (name) => String(name || '').trim().replace(/^(mr|mrs|ms|miss|dr|shri|smt)\.?\s+/i, '').split(/\s+/)[0] || 'there';

/** Store details from the active store object (works with different field names) */
export const storeInfo = (store = {}) => ({
    name: store?.storeName || store?.name || store?.businessName || 'our salon',
    phone: store?.phone || store?.contactNumber || store?.mobile || '',
    address: [store?.address || store?.addressLine1, store?.area || store?.locality, store?.city].filter(Boolean).join(', '),
    mapUrl: store?.mapUrl || store?.googleMapUrl || '',
});

/** Normalised appointment (accepts the API row or the form data) */
const norm = (a = {}) => ({
    client: a.clientName || '',
    phone: a.clientPhone || a.phone || '',
    service: a.serviceSnapshot?.name || a.serviceName || 'your service',
    staff: a.staffName || '',
    date: a.date,
    start: a.startTime || a.start,
    end: a.endTime || a.end,
    amount: Number(a.finalAmount ?? a.amount) || 0,
    notes: a.notes || '',
    no: a.appointmentNo || '',
});

const when = (a) => `${prettyDate(a.date)}, ${prettyTime(a.start)}${a.end ? ` to ${prettyTime(a.end)}` : ''}`;

const footer = (s) => [
    s.address ? `Address: ${s.address}` : '',
    s.mapUrl ? `Location: ${s.mapUrl}` : '',
    s.phone ? `Call us: ${s.phone}` : '',
].filter(Boolean).join('\n');

/* ============================== CLIENT ============================== */
export const CLIENT_MESSAGES = {
    CONFIRMED: { label: 'Send confirmation', short: 'Confirmation' },
    REMINDER: { label: 'Send reminder', short: 'Reminder' },
    RESCHEDULED: { label: 'Send new time', short: 'Rescheduled' },
    CANCELLED: { label: 'Send cancellation', short: 'Cancelled' },
    THANK_YOU: { label: 'Send thank-you', short: 'Thank you' },
};

export const clientMessage = (type, appointment, store) => {
    const a = norm(appointment);
    const s = storeInfo(store);
    const hi = `Hi ${firstName(a.client)},`;
    const details = [
        `Date and time: ${when(a)}`,
        `Service: ${a.service}`,
        a.staff ? `With: ${a.staff}` : '',
        a.no ? `Booking ID: ${a.no}` : '',
    ].filter(Boolean).join('\n');

    switch (type) {
        case 'REMINDER':
            return [hi, '', `This is a reminder of your appointment at ${s.name}.`, '', details, '', 'Please reach 5 minutes early. Reply here if you need to change the time.', footer(s)].join('\n').trim();
        case 'RESCHEDULED':
            return [hi, '', `Your appointment at ${s.name} has been moved to a new time.`, '', details, '', 'Reply here if this time does not work for you.', footer(s)].join('\n').trim();
        case 'CANCELLED':
            return [hi, '', `Your appointment at ${s.name} on ${when(a)} for ${a.service} has been cancelled.`, '', 'Reply here and we will book a new time for you.', s.phone ? `Call us: ${s.phone}` : ''].join('\n').trim();
        case 'THANK_YOU':
            return [hi, '', `Thank you for visiting ${s.name} today. We hope you loved your ${a.service}${a.staff ? ` with ${a.staff}` : ''}.`, '', 'We would love to hear your feedback. Reply here to book your next visit.'].join('\n').trim();
        case 'CONFIRMED':
        default:
            return [hi, '', `Your appointment at ${s.name} is confirmed.`, '', details, '', 'Reply here if you need to change the time. See you soon!', footer(s)].join('\n').trim();
    }
};

/* ============================== STAFF ============================== */
export const staffNewBookingMessage = (appointment, store, staffName) => {
    const a = norm(appointment);
    const s = storeInfo(store);
    const lines = [
        `Hi ${firstName(staffName || a.staff)},`,
        '',
        `New appointment for you at ${s.name}:`,
        '',
        `Client: ${a.client}${a.phone ? ` (${a.phone})` : ''}`,
        `Service: ${a.service}`,
        `Date and time: ${when(a)}`,
    ];
    if (a.notes) lines.push(`Notes: ${a.notes}`);
    return lines.join('\n');
};

/** One staff member's appointments for a day, in time order */
export const staffDayScheduleMessage = (appointments, store, staffName, ymd) => {
    const s = storeInfo(store);
    const list = [...appointments]
        .filter((a) => !['Cancelled', 'No_Show'].includes(a.status))
        .sort((x, y) => String(x.startTime).localeCompare(String(y.startTime)));

    if (!list.length) {
        return `Hi ${firstName(staffName)},\n\nYou have no appointments on ${prettyDate(ymd)} at ${s.name}.`;
    }

    const lines = list.map((raw, i) => {
        const a = norm(raw);
        return `${i + 1}. ${prettyTime(a.start)} to ${prettyTime(a.end)}: ${a.client}, ${a.service}${a.notes ? ` (${a.notes})` : ''}`;
    });

    return [
        `Hi ${firstName(staffName)},`,
        '',
        `Your appointments on ${prettyDate(ymd)} at ${s.name} (${list.length}):`,
        '',
        ...lines,
    ].join('\n');
};

/** Staff phone from different staff object shapes */
export const staffPhoneOf = (staff = {}) => staff?.phone || staff?.mobile || staff?.contactNumber
    || staff?.personalInfo?.phone || staff?.personalInfo?.mobile || staff?.contact?.phone || '';